/**
 * What to honestly say about a course's state, and what to offer about it.
 *
 * The screen used to tell every unfinished course "Still being generated. This
 * takes a while for a full recording." That is true of a job a worker is
 * running. It is a lie about a job nothing ever collected — and those look
 * identical on the card while only one of them is progress.
 *
 * They diverge because Celery acknowledges a message when a worker receives
 * it rather than when it finishes, so a restart silently drops everything
 * prefetched. The row stays `pending` for good, and the old copy kept
 * promising it was nearly there.
 *
 * Pure: no React, no network, so the wording can be tested directly.
 */

export type CourseState = 'pending' | 'running' | 'done' | 'failed';

/**
 * How long a queued job may sit before we stop calling it queued. Matches the
 * server's own STALE_PENDING_SEC, which is what decides whether Generate
 * Course will actually re-dispatch it — saying "retry" while the server
 * refuses would be its own small lie.
 */
export const STALE_PENDING_MS = 10 * 60 * 1000;

/**
 * A run legitimately takes upwards of an hour and writes nothing while it
 * works, so silence is not evidence for a long time.
 */
export const STALE_RUNNING_MS = 6 * 60 * 60 * 1000;

export type CourseLike = {
  status?: CourseState | null;
  uuid?: string | null;
  error?: string | null;
  updated_at?: string | null;
  chapter_count?: number;
};

export type CourseVerdict = {
  /** The chip. */
  label: string;
  /** The sentence under it. Never claims progress it cannot see. */
  detail: string;
  /** Show a retry/rebuild control, and what to call it. */
  action: 'none' | 'retry' | 'rebuild';
  actionLabel: string;
  /**
   * Whether the request must set `force`.
   *
   * Always, whenever a control is shown, and that is not belt-and-braces. The
   * server only re-dispatches an unfinished job when it is `pending` *and*
   * stale — `_is_stale_pending` never expires a `running` row, deliberately,
   * since a real run takes an hour. So a Stalled card without force gets a
   * 200 carrying the same row back and nothing is queued: the button appears
   * to work and does nothing.
   *
   * Forcing cannot start a duplicate, because a control is never offered
   * while a job is genuinely in flight — only once it is stalled, failed, or
   * finished.
   */
  forces: boolean;
  /** True when the run appears abandoned rather than merely slow. */
  stalled: boolean;
};

function ageMs(course: CourseLike, now: number): number | null {
  if (!course.updated_at) return null;
  const at = Date.parse(course.updated_at);
  return Number.isNaN(at) ? null : Math.max(0, now - at);
}

function humanAge(ms: number): string {
  const minutes = Math.floor(ms / 60000);
  if (minutes < 1) return 'less than a minute';
  if (minutes < 60) return `${minutes} minute${minutes === 1 ? '' : 's'}`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours} hour${hours === 1 ? '' : 's'}`;
  const days = Math.floor(hours / 24);
  return `${days} day${days === 1 ? '' : 's'}`;
}

export function describe(course: CourseLike, now = Date.now()): CourseVerdict {
  // Defaulted, not assumed: an API predating the status field would otherwise
  // crash the grid, and a course with a book is by definition finished.
  const status: CourseState =
    (course.status as CourseState) ?? (course.uuid ? 'done' : 'pending');
  const age = ageMs(course, now);

  if (status === 'done' && course.uuid) {
    return {
      label: 'Ready',
      detail: '',
      // Offered even when it worked: a course can be rebuilt after the
      // recording is re-cut, or after the detector improves.
      action: 'rebuild',
      actionLabel: 'Rebuild',
      forces: true,
      stalled: false,
    };
  }

  if (status === 'failed') {
    return {
      label: 'Failed',
      detail: course.error || 'This run failed, and did not say why.',
      action: 'retry',
      actionLabel: 'Try again',
      forces: true,
      stalled: true,
    };
  }

  if (status === 'running') {
    const stalled = age != null && age > STALE_RUNNING_MS;
    return {
      label: stalled ? 'Stalled' : 'Generating…',
      detail: stalled
        ? `Started ${humanAge(age as number)} ago and has not finished. ` +
          `The worker running it has most likely stopped — a run takes about ` +
          `an hour, not this long.`
        : 'Being built now. A full recording takes about an hour.',
      action: stalled ? 'retry' : 'none',
      actionLabel: 'Start again',
      forces: stalled,
      stalled,
    };
  }

  // pending
  const waited = age != null && age > STALE_PENDING_MS;
  return {
    label: waited ? 'Not picked up' : 'Queued',
    detail: waited
      ? `Waiting ${humanAge(age as number)} and no worker has taken it. ` +
        `It is not being built — it needs starting again.`
      : // Honest about what queued means: nothing is happening yet.
        'Waiting for a worker. Nothing is being built yet.',
    action: waited ? 'retry' : 'none',
    actionLabel: 'Start again',
    forces: waited,
    stalled: waited,
  };
}
